import type { ActionTodoViewModel, AgreementModel } from 'api/models';

type AgreementChangeType = 'Fees' | 'Homes' | 'Entity' | 'Address';

/** `replacementChanges` comes from belong-platform#12706; the next `generate-api` adds it to `AgreementModel`. */
type AgreementWithChanges = AgreementModel & {
  replacementChanges?: AgreementChangeType[] | null;
};

export function isAgreementUpdate(
  agreement: AgreementModel | null | undefined,
): boolean {
  return agreement?.replacesAgreementId != null;
}

/** An update without a recorded change list keeps the fee note, so older backends don't lose it. */
export function showsFeeChangeNote(
  agreement: AgreementWithChanges | null | undefined,
): boolean {
  if (!isAgreementUpdate(agreement)) {
    return false;
  }

  return agreement?.replacementChanges?.includes('Fees') ?? true;
}

type SplitSigning = {
  size: number;
  signed: number;
  hasOtherSigners: boolean;
};

export function isSplitUpdate(
  agreement: AgreementModel | null | undefined,
): boolean {
  return (agreement?.replacementGroupSize ?? 0) > 1;
}

/** The viewer's own place in a split: null unless they sign 2+ of its agreements. */
export function getSplitSigning(
  agreement: AgreementModel | null | undefined,
): SplitSigning | null {
  const size = agreement?.replacementGroupViewerSignerCount;

  if (!agreement || !isSplitUpdate(agreement) || size == null || size <= 1) {
    return null;
  }

  return {
    size,
    signed: agreement.replacementGroupViewerSignedCount ?? 0,
    hasOtherSigners: agreement.replacementGroupHasOtherSigners ?? false,
  };
}

/** Open agreement flows of the same update, in to-do order; the caller takes the first with an open step. */
export function getNextAgreementFlowIds(
  todos: ActionTodoViewModel[] | null | undefined,
  current: AgreementModel | null | undefined,
  currentFlowId: string,
): string[] {
  const replacesId = current?.replacesAgreementId;
  const groupId = current?.replacementGroupId;

  if (replacesId == null || groupId == null || !todos) {
    return [];
  }

  return todos
    .filter(
      ({ type, metadata }) =>
        type === 'AgreementFlowOpen' &&
        metadata?.replacesAgreementId === replacesId &&
        metadata.replacementGroup?.id === groupId &&
        metadata.flowId !== currentFlowId,
    )
    .map(({ metadata }) => metadata?.flowId as string);
}

type NextAgreement =
  | { status: 'next'; path: string }
  | { status: 'done' }
  | { status: 'unknown' };

/**
 * Where to go after signing an agreement of a split: the first open step of the next agreement the viewer still
 * signs. A candidate that fails to load is skipped, but if none is found the result is unknown rather than done.
 */
export async function findNextAgreement<TFlow>({
  todos,
  current,
  currentFlowId,
  fetchFlow,
  getOpenStepPath,
}: {
  todos: ActionTodoViewModel[] | null | undefined;
  current: AgreementModel | null | undefined;
  currentFlowId: string;
  fetchFlow: (flowId: string) => Promise<TFlow>;
  getOpenStepPath: (flow: TFlow, flowId: string) => string | null;
}): Promise<NextAgreement> {
  let hasFailedLookup = false;

  // Sequential on purpose: a voided update's flows are cancelled asynchronously, so a candidate can have nothing
  // left to sign and the next one is tried.
  for (const flowId of getNextAgreementFlowIds(todos, current, currentFlowId)) {
    try {
      const path = getOpenStepPath(await fetchFlow(flowId), flowId);

      if (path) {
        return { status: 'next', path };
      }
    } catch {
      hasFailedLookup = true;
    }
  }

  return { status: hasFailedLookup ? 'unknown' : 'done' };
}

export type UpdateSuccess = 'waiting' | 'in-place';

export function getUpdateSuccess(
  agreement: AgreementModel | null | undefined,
): UpdateSuccess | null {
  if (!isAgreementUpdate(agreement)) {
    return null;
  }

  const signedCount = agreement?.replacementGroupSignedCount;

  if (isSplitUpdate(agreement) && signedCount != null) {
    return signedCount >= (agreement?.replacementGroupSize ?? 0)
      ? 'in-place'
      : 'waiting';
  }

  return agreement?.status === 'Signed' ? 'in-place' : 'waiting';
}
